import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Select from 'react-select';
import { Range } from 'react-range';
import toast from 'react-hot-toast';
import { Edit as EditIcon, Save as SaveIcon, X as XIcon } from 'lucide-react';
import { updateCompanyPreferences } from '../../../../services/staffingService';

// Validation schema
const preferencesSchema = yup.object().shape({
  preferredServiceTypes: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one service type must be selected')
    .required(),
  preferredTimeWindows: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one time window must be selected')
    .required(),
  budgetFloor: yup.number().min(45, 'Budget floor must be at least $45').max(150, 'Budget floor cannot exceed $150').required(),
  budgetCeiling: yup.number().min(45, 'Budget ceiling must be at least $45').max(150, 'Budget ceiling cannot exceed $150').required(),
  requireBackgroundCheck: yup.boolean(),
  requireFirearmsCertification: yup.boolean(),
});

const CustomerPreferencesTab = ({ customerCompany, setCustomerCompany }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(preferencesSchema),
    defaultValues: {
      preferredServiceTypes: [],
      preferredTimeWindows: [],
      budgetFloor: 45,
      budgetCeiling: 75,
      requireBackgroundCheck: true,
      requireFirearmsCertification: false,
    },
  });

  const watchedValues = watch();

  // Auto-populate form when customerCompany data arrives
  useEffect(() => {
    if (customerCompany?.preferences) {
      const prefs = customerCompany.preferences;
      setValue('preferredServiceTypes', prefs.preferredServiceTypes || []);
      setValue('preferredTimeWindows', prefs.preferredTimeWindows || []);
      setValue('budgetFloor', prefs.budgetFloor || 45);
      setValue('budgetCeiling', prefs.budgetCeiling || 75);
      setValue('requireBackgroundCheck', prefs.requireBackgroundCheck !== false);
      setValue('requireFirearmsCertification', prefs.requireFirearmsCertification || false);
    }
  }, [customerCompany, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await updateCompanyPreferences(customerCompany._id, data);
      
      // Update parent state with new preferences
      setCustomerCompany({
        ...customerCompany,
        preferences: data
      });
      setIsEditing(false);
      toast.success('Preferences saved successfully');
    } catch (err) {
      console.error('Failed to update preferences:', err);
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const toggleEditMode = () => setIsEditing(!isEditing);

  const serviceTypeOptions = [
    { value: 'Standing', label: 'Standing Post' },
    { value: 'Event', label: 'Event Security' },
    { value: 'Patrol', label: 'Mobile Patrol' },
    { value: 'Storefront', label: 'Storefront Security' },
    { value: 'Workplace', label: 'Workplace Security' },
    { value: 'Traffic', label: 'Traffic Control' },
  ];

  const timeWindowOptions = [
    { value: 'Morning', label: 'Morning (6AM - 12PM)' },
    { value: 'Afternoon', label: 'Afternoon (12PM - 6PM)' },
    { value: 'Evening', label: 'Evening (6PM - 10PM)' },
    { value: 'Night', label: 'Night (10PM - 6AM)' },
  ];

  if (loading && !customerCompany) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#404880]"></div>
      </div>
    );
  }

  const prefs = customerCompany?.preferences || {};

  return (
    <div className="font-inter">
      <style>{`
        .react-range-track {
          background: #e5e7eb;
          height: 6px;
          border-radius: 3px;
        }
        .react-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #404880;
          border: 2px solid #404880;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          cursor: pointer;
        }
        .react-range-thumb:hover {
          box-shadow: 0 2px 8px rgba(64, 72, 128, 0.3);
        }
        .react-range-thumb:focus {
          outline: none;
          box-shadow: 0 0 0 5px rgba(64, 72, 128, 0.2);
        }
      `}</style>
      {!isEditing ? (
        <div className="space-y-6">
          {/* Service Types */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Preferred Service Types</h3>
            <div className="flex flex-wrap gap-2">
              {prefs?.preferredServiceTypes?.length > 0 ? (
                prefs.preferredServiceTypes.map(service => (
                  <span
                    key={service}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {service}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-600">Not configured</p>
              )}
            </div>
          </div>

          {/* Time Windows */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Preferred Time Windows</h3>
            <div className="flex flex-wrap gap-2">
              {prefs?.preferredTimeWindows?.length > 0 ? (
                prefs.preferredTimeWindows.map(time => (
                  <span
                    key={time}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {time}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-600">Not configured</p>
              )}
            </div>
          </div>

          {/* Budget */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Your Budget Range</h3>
            <div className="flex items-center justify-center gap-4">
              <span className="text-2xl font-bold text-[#404880]">
                ${prefs?.budgetFloor || '45'}
              </span>
              <div className="flex-1 h-1 bg-[#404880] rounded-full"></div>
              <span className="text-2xl font-bold text-[#404880]">
                ${prefs?.budgetCeiling || '75'}
              </span>
            </div>
            <p className="text-xs text-gray-500 text-center mt-3">per hour</p>
          </div>

          {/* Officer Requirements */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Officer Requirements</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Background Check Required</span>
                <span className={`text-sm font-semibold ${prefs?.requireBackgroundCheck ? 'text-green-600' : 'text-gray-500'}`}>
                  {prefs?.requireBackgroundCheck ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Firearms Certification Required</span>
                <span className={`text-sm font-semibold ${prefs?.requireFirearmsCertification ? 'text-green-600' : 'text-gray-500'}`}>
                  {prefs?.requireFirearmsCertification ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              onClick={toggleEditMode}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-[#404880] bg-transparent border border-[#404880] rounded-lg hover:bg-[#404880] hover:text-white transition-all duration-200 font-medium text-sm"
            >
              <EditIcon size={16} />
              Edit Preferences
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Service Types */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Preferred Service Types
            </label>
            <Controller
              name="preferredServiceTypes"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  options={serviceTypeOptions}
                  value={field.value?.map(v => serviceTypeOptions.find(opt => opt.value === v)) || []}
                  onChange={(selected) => field.onChange(selected?.map(opt => opt.value) || [])}
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.preferredServiceTypes && (
              <p className="text-xs text-red-500 mt-2">{errors.preferredServiceTypes.message}</p>
            )}
          </div>

          {/* Time Windows */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Preferred Time Windows
            </label>
            <Controller
              name="preferredTimeWindows"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  options={timeWindowOptions}
                  value={field.value?.map(v => timeWindowOptions.find(opt => opt.value === v)) || []}
                  onChange={(selected) => field.onChange(selected?.map(opt => opt.value) || [])}
                  classNamePrefix="react-select"
                />
              )}
            />
            {errors.preferredTimeWindows && (
              <p className="text-xs text-red-500 mt-2">{errors.preferredTimeWindows.message}</p>
            )}
          </div>

          {/* Budget */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-6 text-sm font-semibold text-gray-900">Set Your Budget Range</h3>
            <p className="text-xs text-gray-500 mb-4">Drag either handle—the gap between them stays constant.</p>
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-medium text-gray-600">From: ${watch('budgetFloor')}/hr</span>
                <span className="text-xs font-medium text-gray-600">To: ${watch('budgetCeiling')}/hr</span>
              </div>
              <Controller
                name="budgetFloor"
                control={control}
                render={({ field: floorField }) => (
                  <Controller
                    name="budgetCeiling"
                    control={control}
                    render={({ field: ceilingField }) => {
                      const handleChange = (values) => {
                        const gap = ceilingField.value - floorField.value;
                        const [newFloor, newCeiling] = values;
                        
                        // Maintain constant gap
                        if (newFloor !== floorField.value) {
                          // Left handle moved
                          floorField.onChange(newFloor);
                          ceilingField.onChange(Math.min(newFloor + gap, 150));
                        } else if (newCeiling !== ceilingField.value) {
                          // Right handle moved
                          ceilingField.onChange(newCeiling);
                          floorField.onChange(Math.max(newCeiling - gap, 45));
                        }
                      };

                      return (
                        <Range
                          step={5}
                          min={45}
                          max={150}
                          values={[floorField.value, ceilingField.value]}
                          onChange={handleChange}
                          renderTrack={({ props, children }) => (
                            <div
                              {...props}
                              className="react-range-track"
                              style={{
                                ...props.style,
                                background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${((floorField.value - 45) / 105) * 100}%, #404880 ${((floorField.value - 45) / 105) * 100}%, #404880 ${((ceilingField.value - 45) / 105) * 100}%, #e5e7eb ${((ceilingField.value - 45) / 105) * 100}%, #e5e7eb 100%)`,
                              }}
                            >
                              {children}
                            </div>
                          )}
                          renderThumb={({ props }) => (
                            <div
                              {...props}
                              className="react-range-thumb"
                              style={{
                                ...props.style,
                              }}
                            />
                          )}
                        />
                      );
                    }}
                  />
                )}
              />
            </div>
          </div>

          {/* Officer Requirements */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Officer Requirements</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('requireBackgroundCheck')}
                  className="w-4 h-4 accent-[#404880]"
                />
                <span className="text-sm text-gray-700">Background Check Required</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('requireFirearmsCertification')}
                  className="w-4 h-4 accent-[#404880]"
                />
                <span className="text-sm text-gray-700">Firearms Certification Required</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={toggleEditMode}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium text-sm"
            >
              <XIcon size={16} />
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-white bg-[#404880] rounded-lg hover:bg-[#404880]/90 transition-all duration-200 font-medium text-sm"
            >
              <SaveIcon size={16} />
              Save Preferences
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CustomerPreferencesTab;
